import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CanvasComponent } from './CanvasComponent';
import { Toolbar } from './Toolbar';
import { 
  BackgroundType, 
  ToolType, 
  DrawingState, 
  UIState,
  Line,
  TextNote,
  LineStyle,
  Point
} from './types';
import { HistoryManager } from '../UndoRedo/HistoryManager';

interface RTCMessage {
  type: 'drawing_state' | 'cursor_move' | 'user_action' | 'user_join' | 'user_leave';
  data: any;
  from: string;
  timestamp: number;
}

interface PeerCursor {
  id: string;
  x: number;
  y: number;
  color: string;
  name: string;
  lastUpdate: number;
}

interface CollaborativeCanvasProps {
  roomId: string;
}

export const CollaborativeCanvas: React.FC<CollaborativeCanvasProps> = ({ roomId }) => {
  const [drawingState, setDrawingState] = useState<DrawingState>({ 
    lines: [],
    textNotes: []
  });
  
  const [uiState, setUiState] = useState<UIState>({
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    tool: 'pointer',
    penColor: '#000000',
    penWidth: 3,
    eraserSize: 30,
    lineStyle: 'solid',
    selection: [],
    isSelecting: false,
    isDraggingSelection: false
  });

  const [peerCursors, setPeerCursors] = useState<Map<string, PeerCursor>>(new Map());
  const [roomLink, setRoomLink] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [peerCount, setPeerCount] = useState(1);
  const [localUserId] = useState(() => Math.random().toString(36).substring(2, 15));
  const [userName] = useState(() => `User_${Math.random().toString(36).substring(2, 6)}`);
  
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const dataChannel = useRef<RTCDataChannel | null>(null);
  const historyManager = useRef(new HistoryManager(drawingState));
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastSentState = useRef<string>('');

  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3'];

  // Генерация уникальной ссылки
  const generateRoomLink = useCallback(() => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?room=${roomId}`;
  }, [roomId]);

  // Отправка сообщений
  const sendMessage = useCallback((type: RTCMessage['type'], data: any) => {
    if (dataChannel.current?.readyState === 'open') {
      const message: RTCMessage = {
        type,
        data,
        from: localUserId,
        timestamp: Date.now()
      };
      
      // Дебаунс для состояния рисования
      if (type === 'drawing_state') {
        const stateString = JSON.stringify(data);
        if (stateString === lastSentState.current) return;
        lastSentState.current = stateString;
      }
      
      dataChannel.current.send(JSON.stringify(message));
    }
  }, [localUserId]);

  // Инициализация WebRTC
  const initWebRTC = useCallback(async () => {
    const config = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };

    peerConnection.current = new RTCPeerConnection(config);
    
    // Создание data channel
    dataChannel.current = peerConnection.current.createDataChannel('drawing', {
      ordered: true,
      maxRetransmits: 3
    });

    dataChannel.current.onopen = () => {
      console.log('✅ Connected to peer');
      setIsConnected(true);
      setPeerCount(2);
      sendMessage('user_join', { name: userName });
      sendMessage('drawing_state', drawingState);
    };

    dataChannel.current.onmessage = (event) => {
      try {
        const message: RTCMessage = JSON.parse(event.data);
        handleMessage(message);
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    };

    dataChannel.current.onerror = (error) => {
      console.error('Data channel error:', error);
    };

    dataChannel.current.onclose = () => {
      console.log('❌ Disconnected from peer');
      setIsConnected(false);
      setPeerCount(1);
      
      // Автоматическая переподключение
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = setTimeout(() => {
        initWebRTC();
      }, 3000);
    };

    // Обработка входящих соединений
    peerConnection.current.ondatachannel = (event) => {
      dataChannel.current = event.channel;
      dataChannel.current.onopen = () => {
        console.log('✅ Peer connected');
        setIsConnected(true);
        setPeerCount(2);
        sendMessage('drawing_state', drawingState);
      };
      dataChannel.current.onmessage = (event) => {
        const message: RTCMessage = JSON.parse(event.data);
        handleMessage(message);
      };
    };

    // Обработка ICE кандидатов
    peerConnection.current.onicecandidate = (event) => {
      if (event.candidate) {
        // В реальном приложении отправка через сигналинг сервер
        localStorage.setItem('webrtc_ice', JSON.stringify(event.candidate));
      }
    };
  }, [userName, drawingState]);

  const handleMessage = useCallback((message: RTCMessage) => {
    switch (message.type) {
      case 'drawing_state':
        setDrawingState(message.data);
        historyManager.current = new HistoryManager(message.data);
        break;
        
      case 'cursor_move':
        setPeerCursors(prev => {
          const newMap = new Map(prev);
          newMap.set(message.from, {
            id: message.from,
            ...message.data,
            color: colors[message.from.charCodeAt(0) % colors.length],
            lastUpdate: Date.now()
          });
          return newMap;
        });
        break;
        
      case 'user_join':
        setPeerCount(prev => prev + 1);
        break;
        
      case 'user_leave':
        setPeerCount(prev => Math.max(1, prev - 1));
        setPeerCursors(prev => {
          const newMap = new Map(prev);
          newMap.delete(message.from);
          return newMap;
        });
        break;
    }
  }, []);

  // Очистка старых курсоров
  useEffect(() => {
    const interval = setInterval(() => {
      setPeerCursors(prev => {
        const now = Date.now();
        const newMap = new Map(prev);
        Array.from(prev.keys()).forEach(id => {
          const cursor = prev.get(id);
          if (cursor && now - cursor.lastUpdate > 5000) {
            newMap.delete(id);
          }
        });
        return newMap;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Инициализация WebRTC
  useEffect(() => {
    initWebRTC();

    return () => {
      if (dataChannel.current) {
        dataChannel.current.close();
      }
      if (peerConnection.current) {
        peerConnection.current.close();
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
    };
  }, [roomId, initWebRTC]);

  const handleDrawingStateChange = useCallback((newState: DrawingState) => {
    setDrawingState(newState);
    historyManager.current.push(newState);
    sendMessage('drawing_state', newState);
  }, [sendMessage]);

  const copyRoomLink = () => {
    if (roomLink) {
      navigator.clipboard.writeText(roomLink).then(() => {
        alert('Ссылка скопирована в буфер обмена!');
      });
    }
  };

  return (
    <div className="collaborative-canvas">
      <div className="collab-header">
        <div className="room-info">
          <span className="room-title">Комната: {roomId}</span>
          <div className="connection-status">
            {isConnected ? (
              <span className="connected">✅ Подключено ({peerCount} участников)</span>
            ) : (
              <span className="connecting">🔗 Подключение...</span>
            )}
          </div>
        </div>
        
        {roomLink && (
          <div className="room-link-container">
            <span>Поделиться:</span>
            <input 
              type="text" 
              value={roomLink} 
              readOnly 
              className="room-link-input"
            />
            <button onClick={copyRoomLink} className="copy-btn">
              📋 Копировать
            </button>
          </div>
        )}
      </div>

      {/* Курсоры других пользователей */}
      <div className="peer-cursors">
        {Array.from(peerCursors.values()).map((cursor) => (
          <div
            key={cursor.id}
            className="peer-cursor"
            style={{
              position: 'absolute',
              left: cursor.x * 1 - 0, // Упрощенная позиция
              top: cursor.y * 1 - 0,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 1000
            }}
          >
            <div 
              className="cursor-pointer"
              style={{
                width: 12,
                height: 12,
                backgroundColor: cursor.color,
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 0 4px rgba(0,0,0,0.3)'
              }}
            />
            <div 
              className="cursor-name"
              style={{
                position: 'absolute',
                top: -20,
                left: 10,
                backgroundColor: cursor.color,
                color: 'white',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '12px',
                whiteSpace: 'nowrap'
              }}
            >
              {cursor.name}
            </div>
          </div>
        ))}
      </div>

      <div className="canvas-container-wrapper">
        <CanvasComponent 
          backgroundType={'grid'}
          drawingState={drawingState}
          uiState={uiState}
          onUiStateChange={setUiState}
          onAddLine={(line) => handleDrawingStateChange({ ...drawingState, lines: [...drawingState.lines, line] })}
          onDeleteLine={(lineId) => handleDrawingStateChange({ ...drawingState, lines: drawingState.lines.filter(l => l.id !== lineId) })}
          onUpdateLines={(lines, textNotes) => handleDrawingStateChange({ lines, textNotes: textNotes || [] })}
        />
      </div>
    </div>
  );
};

// Добавим CSS стили в файл styles.css
const style = document.createElement('style');
style.textContent = `
  .collaborative-canvas {
    height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .collab-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 15px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    flex-wrap: wrap;
    gap: 10px;
  }

  .room-info {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  .room-title {
    font-size: 16px;
    font-weight: bold;
  }

  .connection-status {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
  }

  .connected {
    background: #4CAF50;
  }

  .connecting {
    background: #FF9800;
  }

  .room-link-container {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .room-link-input {
    padding: 8px 12px;
    border: none;
    border-radius: 4px;
    background: rgba(255,255,255,0.2);
    color: white;
    min-width: 250px;
  }

  .copy-btn {
    padding: 8px 16px;
    background: rgba(255,255,255,0.2);
    border: none;
    border-radius: 4px;
    color: white;
    cursor: pointer;
    transition: background 0.3s;
  }

  .copy-btn:hover {
    background: rgba(255,255,255,0.3);
  }

  .canvas-container-wrapper {
    flex: 1;
    position: relative;
    overflow: hidden;
  }

  .peer-cursors {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 999;
  }

  .peer-cursor {
    transition: all 0.1s ease;
  }

  .cursor-pointer {
    position: absolute;
  }

  .cursor-name {
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  @media (max-width: 768px) {
    .collab-header {
      flex-direction: column;
      align-items: stretch;
      text-align: center;
    }
    
    .room-link-container {
      flex-direction: column;
      align-items: stretch;
    }
    
    .room-link-input {
      min-width: auto;
    }
  }
`;
document.head.appendChild(style);

export default CollaborativeCanvas;